import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  patchState,
  signalStore,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap, tap } from 'rxjs';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import {
  AcademyMeteringState,
  initialAcademyMeteringState,
} from './entities/academy-metering-store.model';
import { UsageMetricResponse } from './entities/academy.model';
import { MeteringCloseParams } from './entities/academy-query.model';
import { MeteringService } from './infra/metering.service';

const ALL_OPS = ['loadUsage', 'loadWindow', 'aggregate', 'close'] as const;

export const AcademyMeteringStore = signalStore(
  { providedIn: 'root' },
  withState<AcademyMeteringState>(initialAcademyMeteringState),
  withReqState(...ALL_OPS),
  withMethods(
    (
      store,
      metering = inject(MeteringService),
      errorStore = inject(ErrorStore),
    ) => {
      const loadUsage = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadUsage'))),
          switchMap((period) =>
            metering.usage(period).pipe(
              tapResponse({
                next: (items: UsageMetricResponse[]) =>
                  patchState(
                    store,
                    {
                      usageByPeriod: {
                        ...store.usageByPeriod(),
                        [period]: items,
                      },
                    },
                    setPropLoaded('loadUsage'),
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadUsage', error));
                },
              }),
            ),
          ),
        ),
      );

      /** Merges a `{ [period]: rows }` map into the cache (later periods overwrite earlier ones). */
      const mergeWindow = (
        byPeriod: Record<string, UsageMetricResponse[]>,
      ): Record<string, UsageMetricResponse[]> => ({
        ...store.usageByPeriod(),
        ...byPeriod,
      });

      const loadWindow = rxMethod<string[]>(
        pipe(
          tap((periods) =>
            patchState(store, { window: periods }, setPropLoading('loadWindow')),
          ),
          switchMap((periods) =>
            metering.usageWindow(periods).pipe(
              tapResponse({
                next: (byPeriod) =>
                  patchState(
                    store,
                    { usageByPeriod: mergeWindow(byPeriod) },
                    setPropLoaded('loadWindow'),
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadWindow', error));
                },
              }),
            ),
          ),
        ),
      );

      // `aggregate`/`close` are idempotent commands; after a 200 they re-read the remembered
      // window so the figures on screen reflect the recomputed snapshots (single emission —
      // command then window read are chained inside one pipe, never a self-cancelling re-dispatch).
      const aggregate = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('aggregate'))),
          switchMap((period) =>
            metering.aggregate(period).pipe(
              switchMap(() => metering.usageWindow(store.window())),
              tapResponse({
                next: (byPeriod) =>
                  patchState(
                    store,
                    { usageByPeriod: mergeWindow(byPeriod) },
                    setPropLoaded('aggregate'),
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('aggregate', error));
                },
              }),
            ),
          ),
        ),
      );

      const close = rxMethod<MeteringCloseParams>(
        pipe(
          tap(() => patchState(store, setPropLoading('close'))),
          switchMap(({ period, ownerType, ownerId }) =>
            metering.close(period, ownerType, ownerId).pipe(
              switchMap(() => metering.usageWindow(store.window())),
              tapResponse({
                next: (byPeriod) =>
                  patchState(
                    store,
                    { usageByPeriod: mergeWindow(byPeriod) },
                    setPropLoaded('close'),
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('close', error));
                },
              }),
            ),
          ),
        ),
      );

      const reset = (): void =>
        patchState(store, initialAcademyMeteringState, setPropInit(...ALL_OPS));

      return { loadUsage, loadWindow, aggregate, close, reset };
    },
  ),
);
